import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ShieldCheck, Ticket, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatINR, pimg, errMsg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const field = (name, label, req = true) => ({ name, label, req });
const FIELDS = [
  field("full_name", "Full Name"),
  field("phone", "Phone Number"),
  field("email", "Email"),
  field("address", "Complete Delivery Address"),
  field("city", "City"),
  field("state", "State"),
  field("pincode", "Pincode"),
];

const Checkout = () => {
  const navigate = useNavigate();
  const { items, subtotal, grams } = useCart();
  const { settings } = useSettings();
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", address: "", city: "", state: "", pincode: "", delivery_instructions: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);

  useEffect(() => {
    if (items.length === 0) navigate("/cart");
    window.scrollTo(0, 0);
  }, []);

  const perKg = settings?.delivery_per_kg ?? 70;
  const kg = grams ? Math.max(1, Math.ceil(grams / 1000)) : 1;
  const delivery = kg * perKg;
  const discount = applied ? applied.discount_amount : 0;
  const total = subtotal + delivery - discount;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setCouponBusy(true); setCouponErr("");
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon.trim(), subtotal });
      setApplied(data);
      toast.success(`Coupon ${data.code} applied — ${data.discount_percent}% off`);
    } catch (e) {
      setApplied(null);
      setCouponErr(errMsg(e));
    } finally { setCouponBusy(false); }
  };

  const validate = () => {
    const e = {};
    FIELDS.forEach((f) => { if (f.req && !form[f.name].trim()) e[f.name] = "Required"; });
    if (form.email && !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) e.email = "Invalid email";
    if (form.phone && !/^[0-9+\-\s]{8,15}$/.test(form.phone)) e.phone = "Invalid phone";
    if (form.pincode && !/^[0-9]{5,6}$/.test(form.pincode)) e.pincode = "Invalid pincode";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) { toast.error("Please fix the highlighted fields"); return; }
    setLoading(true);
    try {
      const payload = { ...form, coupon_code: applied?.code || "", items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, customization: i.customization || null, custom_name: i.custom_name || null, eggless: !!i.eggless })) };
      const { data: order } = await api.post("/orders", payload);
      navigate(`/payment/${order.order_id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10 sm:py-14">
      <h1 className="font-display text-4xl font-bold text-choco mb-8">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#EFEBE1] p-6 sm:p-8">
          <div className="mb-6 bg-cream rounded-xl p-4 text-sm text-chocodark border border-[#E5DFD3]">
            <p className="font-semibold text-choco mb-1">Delivery & Shipping</p>
            <p>Shipping {settings?.shipping_area || "All over Karnataka"} · usually {settings?.delivery_days || "6-7 days"} · via {settings?.couriers || "DTDC and India Post"}.</p>
            <p>Delivery charge ₹{perKg}/kg. <span className="font-medium">No Cash on Delivery — online payment only.</span></p>
          </div>
          <h2 className="font-display text-2xl font-bold text-choco mb-6">Delivery Details</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.name === "address" ? "sm:col-span-2" : ""}>
                <label className="text-sm font-medium text-choco">{f.label}</label>
                {f.name === "address" ? (
                  <Textarea
                    data-testid={`checkout-${f.name}`}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="mt-1.5 bg-cream border-[#E5DFD3] min-h-[80px]"
                  />
                ) : (
                  <Input
                    data-testid={`checkout-${f.name}`}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="mt-1.5 h-12 bg-cream border-[#E5DFD3]"
                  />
                )}
                {errors[f.name] && <p className="text-xs text-destructive mt-1">{errors[f.name]}</p>}
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-choco">Delivery Instructions (optional)</label>
              <Textarea
                data-testid="checkout-delivery_instructions"
                value={form.delivery_instructions}
                onChange={(e) => setForm({ ...form, delivery_instructions: e.target.value })}
                className="mt-1.5 bg-cream border-[#E5DFD3]"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-[#EFEBE1] p-6 sticky top-24">
            <h2 className="font-display text-2xl font-bold text-choco mb-5">Order Summary</h2>
            <div className="space-y-3 max-h-52 overflow-y-auto no-scrollbar mb-4">
              {items.map((i) => (
                <div key={i.key || i.product_id} className="flex gap-3">
                  <img src={pimg(i.image)} alt={i.name} className="h-12 w-12 rounded-lg object-cover" />                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-choco truncate">{i.name}</p>
                    <p className="text-xs text-muted-foreground">Qty {i.quantity}</p>
                  </div>
                  <span className="text-sm font-semibold text-choco">{formatINR(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-[#EFEBE1] pt-4 space-y-2 text-sm">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Ticket size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    data-testid="coupon-input"
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                    disabled={!!applied}
                    className="w-full h-11 rounded-xl bg-cream border border-[#E5DFD3] pl-10 pr-3 text-sm font-mono uppercase outline-none focus:ring-2 focus:ring-gold/40 disabled:opacity-60"
                  />
                </div>
                {applied ? (
                  <button data-testid="coupon-remove" onClick={() => { setApplied(null); setCoupon(""); setCouponErr(""); }} className="h-11 inline-flex items-center gap-1 rounded-xl border border-[#E5DFD3] px-4 text-sm text-destructive hover:opacity-70"><X size={15} /> Remove</button>
                ) : (
                  <button data-testid="coupon-apply" onClick={applyCoupon} disabled={couponBusy || !coupon.trim()} className="h-11 rounded-xl bg-choco text-cream text-sm font-semibold px-5 hover:bg-chocodark disabled:opacity-60">
                    {couponBusy ? <Loader2 size={16} className="animate-spin" /> : "Apply"}
                  </button>
                )}
              </div>
              {couponErr && <p data-testid="coupon-error" className="text-xs text-destructive">{couponErr}</p>}
              {applied && <p data-testid="coupon-success" className="text-xs text-green-700">Coupon {applied.code} applied — {applied.discount_percent}% off</p>}
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold text-choco">{formatINR(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Delivery ({kg} kg × {formatINR(perKg)})</span><span className="font-semibold text-choco">{formatINR(delivery)}</span></div>
              {applied && (
                <div className="flex justify-between"><span className="text-green-700">Discount ({applied.code})</span><span data-testid="checkout-discount" className="font-semibold text-green-700">− {formatINR(discount)}</span></div>
              )}
              <div className="border-t border-[#EFEBE1] pt-2 flex justify-between items-center">
                <span className="font-semibold text-choco">Total</span>
                <span data-testid="checkout-total" className="font-display text-2xl font-bold text-choco">{formatINR(total)}</span>
              </div>
            </div>
            <button
              data-testid="place-order-btn"
              onClick={submit}
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-choco text-cream font-semibold px-6 py-3.5 hover:bg-chocodark active:scale-95 transition-[background-color,transform] disabled:opacity-60"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Placing order...</> : <><Lock size={16} /> Continue to Payment · {formatINR(total)}</>}
            </button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground mt-3">
              <ShieldCheck size={14} className="text-green-700" /> Secure online payment. No COD.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
